/**
 * Camlin Companion — page-aware greeting refresh client (v6, 7 Aug 2026).
 *
 * Calls schema-service's `POST /generate/companion-greeting` (live since
 * the 6 Aug page-context round; runtime-service already uses it at
 * session open) so an idle widget can swap its pending greeting when the
 * visitor SPA-navigates to a page with its own greeting rule.
 *
 * Base URL: `CompanionSiteConfig.companionApiUrl` when set, otherwise
 * SAME-ORIGIN relative — on the speech site the shared ALB routes
 * `/generate/*` to schema-service directly (rule 102, host-agnostic), so
 * the same origin the page loaded from serves this endpoint with no CORS
 * at all. Properties whose edge doesn't route `/generate/*` fail soft:
 * a null result means "keep the greeting you have".
 */

import type { CompanionPageContext } from './types';

export const COMPANION_GREETING_PATH = '/generate/companion-greeting';

export interface FetchCompanionGreetingParams {
  siteKey: string;
  pageContext: CompanionPageContext;
  /** Base URL override; '' / undefined = same-origin relative fetch. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Abort budget — a greeting is cosmetic; never let it hang. */
  timeoutMs?: number;
}

export const COMPANION_GREETING_TIMEOUT_MS = 4000;

/** Resolves the page-appropriate greeting, or null (no rule / any failure). Never throws. */
export async function fetchCompanionGreeting(
  params: FetchCompanionGreetingParams
): Promise<string | null> {
  const fetchImpl = params.fetchImpl ?? fetch;
  const base = (params.baseUrl ?? '').replace(/\/$/, '');
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), params.timeoutMs ?? COMPANION_GREETING_TIMEOUT_MS)
    : null;

  try {
    const response = await fetchImpl(`${base}${COMPANION_GREETING_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteKey: params.siteKey, pageContext: params.pageContext }),
      ...(controller ? { signal: controller.signal } : {})
    });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as { greeting?: string | null };
    return typeof payload.greeting === 'string' && payload.greeting.trim().length > 0
      ? payload.greeting
      : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
